import { Payload, SIWS } from '@web3auth/sign-in-with-solana'
import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes'
import { WalletContextState } from '@solana/wallet-adapter-react'
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js'
import { notify } from './notifications'
import { isUsersApiEnabled, USERS_API_URL } from './constants'
import { NonNullishProps } from 'types/utils'

export interface UserResponse {
  wallet: string
  username: string
}

const MEMO_PROGRAM_ID = new PublicKey(
  'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr',
)

const PAYLOAD_STATEMENT = 'Login to Cabana'
const PAYLOAD_VERSION = '1'
const PAYLOAD_CHAIN_ID = 1

type WalletProps = NonNullishProps<
  Pick<WalletContextState, 'publicKey' | 'signMessage' | 'signTransaction'>
>

export async function login(
  loginType: 'siws' | 'ledger',
  { publicKey, signMessage, signTransaction }: WalletProps,
  connection: Connection,
  translations: { errorTitle: string },
) {
  if (!publicKey || !signMessage || !signTransaction) {
    throw new Error(
      'Wallet not connected and/or missing signMessage/signTransaction method',
    )
  }

  try {
    if (loginType === 'siws') {
      return await authenticateWithSIWS(publicKey, signMessage)
    } else {
      return await authenticateWithLedger(
        publicKey,
        signTransaction,
        connection,
      )
    }
  } catch (e) {
    console.error(`Login error: loginType=${loginType}`, e)
    notify({
      type: 'error',
      title: translations.errorTitle,
      description:
        e != null &&
        typeof e === 'object' &&
        'message' in e &&
        typeof e.message === 'string'
          ? e.message
          : String(e),
    })
  }
}

async function authenticateWithSIWS(
  walletPublicKey: PublicKey,
  signMessage: NonNullable<WalletContextState['signMessage']>,
) {
  const { payload, siwsMessageText } = createSIWSMessage(walletPublicKey)

  const messageSignature = await signMessage(
    new TextEncoder().encode(siwsMessageText),
  )
  return await makeAuthRequest(payload, {
    type: 'signature',
    signatureString: bs58.encode(messageSignature),
  })
}

async function authenticateWithLedger(
  walletPublicKey: PublicKey,
  signTransaction: NonNullable<WalletContextState['signTransaction']>,
  connection: Connection,
) {
  const { payload, siwsMessageText } = createSIWSMessage(walletPublicKey)

  const tx = new Transaction()
  tx.add(
    new TransactionInstruction({
      programId: MEMO_PROGRAM_ID,
      keys: [],
      data: Buffer.from(siwsMessageText),
    }),
  )
  tx.feePayer = walletPublicKey
  tx.recentBlockhash = (
    await connection.getLatestBlockhash('finalized')
  ).blockhash

  const signedTx = await signTransaction(tx)
  const serializedTx = signedTx.serialize()
  return await makeAuthRequest(payload, {
    type: 'ledgerSignedTransaction',
    serializedTx: Array.from(serializedTx),
  })
}

export interface AuthResponse {
  token: string
  user: UserResponse | null
}

async function makeAuthRequest(
  payload: Payload,
  auth:
    | { type: 'signature'; signatureString: string }
    | { type: 'ledgerSignedTransaction'; serializedTx: number[] },
) {
  if (!isUsersApiEnabled()) {
    throw new Error('Users service is not configured')
  }
  const response = await fetch(`${USERS_API_URL}/auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      payload,
      auth,
    }),
  })

  if (!response.ok) {
    const { error } = (await response.json()) as { error: string }
    throw new Error(
      error ?? 'Failed to authenticate: error code ' + response.status,
    )
  }

  return (await response.json()) as AuthResponse
}

function createSIWSMessage(walletPublicKey: PublicKey) {
  const payload = new Payload()
  payload.domain = window.location.host
  payload.address = walletPublicKey.toBase58()
  payload.uri = window.location.origin
  payload.statement = PAYLOAD_STATEMENT
  payload.version = PAYLOAD_VERSION
  payload.chainId = PAYLOAD_CHAIN_ID

  const siwsMessage = new SIWS({ payload })

  return {
    payload,
    siwsMessageText: siwsMessage.prepareMessage(),
  }
}
